import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import {
  Brain, Database, Shield, Cpu, Cloud, BarChart2,
  Users, Zap, ChevronRight, Clock
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const Applications = () => {
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  const researchAreas = [
    {
      icon: <Brain className="w-8 h-8 text-yellow-400" />,
      title: 'AI & Machine Learning',
      description: 'Advanced algorithms and models for intelligent systems'
    },
    {
      icon: <Database className="w-8 h-8 text-yellow-400" />,
      title: 'Data Analytics',
      description: 'Extracting insights from complex datasets'
    },
    {
      icon: <Shield className="w-8 h-8 text-yellow-400" />,
      title: 'Cybersecurity',
      description: 'Protecting systems and networks from digital attacks'
    },
    {
      icon: <Cpu className="w-8 h-8 text-yellow-400" />,
      title: 'Edge Computing',
      description: 'Distributed computing at the edge of the network'
    },
    {
      icon: <Cloud className="w-8 h-8 text-yellow-400" />,
      title: 'Cloud Solutions',
      description: 'Scalable and secure cloud infrastructure'
    },
    {
      icon: <BarChart2 className="w-8 h-8 text-yellow-400" />,
      title: 'Predictive Analytics',
      description: 'Forecasting future trends and behaviors'
    }
  ];

  const projects = [
    {
      title: 'AI-Powered Threat Detection',
      description: 'Developing machine learning models to detect and prevent cybersecurity threats in real-time.',
      icon: <Shield className="w-5 h-5 text-yellow-400" />,
      status: 'In Progress',
      team: 'Security Team',
      date: 'Jan 2024 - Present'
    },
    {
      title: 'Data Visualization Platform',
      description: 'Interactive dashboard for visualizing complex datasets with real-time analytics capabilities.',
      icon: <BarChart2 className="w-5 h-5 text-yellow-400" />,
      status: 'Completed',
      team: 'Data Science',
      date: 'Aug 2023 - Dec 2023'
    },
    {
      title: 'Edge Computing Framework',
      description: 'Optimizing AI model deployment on edge devices for low-latency applications.',
      icon: <Cpu className="w-5 h-5 text-yellow-400" />,
      status: 'In Progress',
      team: 'Edge Computing',
      date: 'Mar 2024 - Present'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-[#2c2c2c] overflow-x-hidden w-full">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -right-40 w-96 h-96 bg-slate-200/40 rounded-full filter blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 w-96 h-96 bg-slate-100/60 rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="text-center"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-white px-4 py-1.5 rounded border border-slate-200"
                variants={fadeInUp}
              >
                <Users className="w-4 h-4 text-[#002b5c]" />
                <span className="text-[#002b5c] text-sm font-medium tracking-wider">RESEARCH & APPLICATIONS</span>
              </motion.div>

              <h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
              >
                <span className="text-[#002b5c]">Our Research</span>
              </h1>

              <motion.p
                className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
                variants={fadeInUp}
              >
                At RAKFORT, we're building the next generation of AI governance solutions to help organizations harness the power of artificial intelligence responsibly and effectively.
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Research */}
      <section className="relative py-12 sm:py-16 px-4 sm:px-6 bg-[#f0f7ff] border-y border-slate-200 overflow-hidden">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            animate={controls}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Our Research Focus Areas</h2>
            <div className="w-16 h-0.5 bg-[#002b5c] mx-auto rounded-full mb-6"></div>
            <p className="text-sm sm:text-base text-slate-500 max-w-2xl mx-auto">
              We're pioneering cutting-edge research across multiple domains to drive innovation and solve complex challenges in cybersecurity and artificial intelligence.
            </p>
          </motion.div>

          <div className="space-y-6 sm:space-y-8">
            {researchAreas.map((area, index) => (
              <motion.div
                key={index}
                className="border border-slate-200 rounded-lg group bg-white shadow-sm"
                initial="hidden"
                animate={controls}
                variants={fadeInUp}
              >
                <div className="bg-white rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row items-start">
                    <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-100 rounded flex items-center justify-center border border-slate-200">
                        {React.cloneElement(area.icon, { className: 'w-6 h-6 sm:w-7 sm:h-7 text-[#002b5c]' })}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{area.title}</h3>
                      <p className="text-sm text-slate-500 mb-3">{area.description}</p>
                      <Link
                        to="/research"
                        className="inline-flex items-center text-sm text-[#005a9c] hover:text-[#004080] font-medium group-hover:translate-x-1 transition-transform"
                      >
                        Learn more <ChevronRight className="w-3.5 h-3.5 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Projects */}
      <section className="relative py-12 sm:py-16 px-4 sm:px-6 bg-white border-y border-slate-200 overflow-hidden">
        <div className="max-w-4xl mx-auto w-full">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            animate={controls}
            variants={fadeInUp}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Latest Research Projects</h2>
            <div className="w-16 h-0.5 bg-[#002b5c] mx-auto rounded-full mb-6"></div>
            <p className="text-sm sm:text-base text-slate-500 max-w-2xl mx-auto">
              Explore our cutting-edge research initiatives that are shaping the future of technology and security.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
                initial="hidden"
                animate={controls}
                variants={fadeInUp}
                whileHover={{ y: -3 }}
              >
                <div className="h-full bg-white rounded-lg p-5">
                  <div className="flex items-center mb-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg mr-3">
                      {React.cloneElement(project.icon, { className: 'w-4 h-4 text-yellow-400' })}
                    </div>
                    <div className="text-xs font-medium text-yellow-400 bg-yellow-400/10 px-2.5 py-1 rounded-full">
                      {project.status}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-sm text-slate-500 mb-4">{project.description}</p>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-800">
                    <div className="flex items-center space-x-1.5">
                      <Users className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs text-gray-500">{project.team}</span>
                    </div>
                    <div className="flex items-center space-x-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-xs text-gray-500">{project.date}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-6 bg-[#f0f7ff] border-t border-slate-200 overflow-hidden">
        <div className="max-w-3xl mx-auto text-center w-full px-4">
          <motion.div
            className="relative z-10"
            initial="hidden"
            animate={controls}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded border border-slate-200 mb-5">
              <Zap className="w-3.5 h-3.5 text-[#002b5c]" />
              <span className="text-[#002b5c] text-xs font-medium tracking-wider">GET INVOLVED</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Join Our Research Community</h2>
            <div className="w-16 h-0.5 bg-[#002b5c] mx-auto rounded-full mb-6"></div>
            <p className="text-sm sm:text-base text-slate-600 mb-8 max-w-2xl mx-auto">
              Be part of groundbreaking research that's shaping the future of technology. Collaborate with our team of experts and contribute to innovative solutions.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a
                href="/contactus"
                className="px-6 py-2.5 bg-[#002b5c] text-white text-sm font-medium rounded border border-[#001f45] hover:bg-[#001f45] transition-colors inline-flex items-center justify-center"
              >
                Contact Our Team
              </a>
              <Link
                to="/research"
                className="px-6 py-2.5 bg-white border border-slate-300 text-slate-800 text-sm font-medium rounded hover:bg-slate-50 transition-colors inline-flex items-center justify-center"
              >
                View Publications <ChevronRight className="w-3.5 h-3.5 ml-1.5" />
              </Link>
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default Applications;